import { useCallback, useEffect, useState } from 'react'
import request from 'tools/request'
import { getCreditCostsOverview } from 'actions/applicants'
import { useAppContext } from 'context/AppContext'

const isCompleted = search => ['COMPLETED', 'SUCCESS'].includes(search?.status?.toUpperCase())

const byNewest = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)

// Extensive FMCSA searches for an application: saved history, a paid "request new", and loading one by id.
const useExtensiveFmcsa = ({ applicationId, enabled, costsEnabled = enabled, creditUsageEnabled, onRequested }) => {
    const { isDemoMode } = useAppContext()
    const [searches, setSearches] = useState([])
    const [loadingList, setLoadingList] = useState(false)
    const [requesting, setRequesting] = useState(false)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [costs, setCosts] = useState([])

    const call = useCallback((path, method) => request(
        `applications/${applicationId}/fmcsa/extensive-search${path}`,
        method, null, false, { 'x-demo-mode': isDemoMode }, null, false
    ), [applicationId, isDemoMode])

    const fetchList = useCallback(async () => {
        setLoadingList(true)
        try {
            const res = await call('', 'GET')
            setSearches([...(res || [])].sort(byNewest))
        } catch (e) {
            setSearches([])
        } finally {
            setLoadingList(false)
        }
    }, [call])

    useEffect(() => {
        if (enabled && applicationId) fetchList()
    }, [enabled, applicationId, fetchList])

    useEffect(() => {
        if (!costsEnabled || !applicationId || !creditUsageEnabled) return
        getCreditCostsOverview(applicationId)()
            .then(res => setCosts(res || []))
            .catch(() => setCosts([]))
    }, [costsEnabled, applicationId, creditUsageEnabled])

    const costOf = requestType => {
        if (!creditUsageEnabled) return null
        return costs.find(c => c.requestType === requestType)?.creditCost ?? null
    }

    // Resolves to { data, search } or null.
    const requestNew = async () => {
        setRequesting(true)
        try {
            const data = await call('', 'POST')
            await fetchList()
            if (onRequested) onRequested()
            return data ? { data, search: { createdAt: new Date().toISOString() } } : null
        } catch (e) {
            return null
        } finally {
            setRequesting(false)
        }
    }

    const open = async search => {
        if (!search?.uuid || !isCompleted(search)) return null
        setLoadingDetail(true)
        try {
            const data = await call(`/${search.uuid}`, 'GET')
            return data ? { data, search } : null
        } catch (e) {
            return null
        } finally {
            setLoadingDetail(false)
        }
    }

    return {
        searches,
        latest: searches.find(isCompleted) || null,
        loadingList,
        requesting,
        loadingDetail,
        extensiveCost: costOf('FMCSA_EXTENSIVE_SEARCH'),
        searchCost: costOf('FMCSA_SEARCH'),
        requestNew,
        open,
        isCompleted,
    }
}

export default useExtensiveFmcsa
